import { gql, useQuery } from '@apollo/client';
import { sortBy } from 'lodash';

import { BillProjectHoursRequestFilter } from '@work4all/models/lib/Classes/BillProjectHoursRequestFilter.entity';
import { Contract } from '@work4all/models/lib/Classes/Contract.entity';
import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { ProjectHoursBillingBy } from '@work4all/models/lib/Enums/ProjectHoursBillingBy.enum';

const CUSTOMERS_QUERY = gql`
  query GetCustomersRelatedToProjectBilling(
    $filters: BillProjectHoursRequestFilter!
  ) {
    getCustomersRelatedToProjectBilling(filters: $filters) {
      id: code
      name: name
      number: nummer
    }
  }
`;

const ORDER_CONFIRMATION_QUERY = gql`
  query GetOrderConfirmationsRelatedToProjectBilling(
    $filters: BillProjectHoursRequestFilter!
  ) {
    getOrderConfirmationsRelatedToProjectBilling(filters: $filters) {
      id: code
      contractDate: auftragsDatum
      contractNumber: auftragsNummer
      note: notiz
      businessPartnerContactCombined: hauptadresse {
        businessPartner: geschaeftspartner {
          id: code
          sdObjType: sdObjType
          data: data {
            ... on Kunde {
              id: code
              number: nummer
              name: name
            }
          }
        }
      }
    }
  }
`;

type QueryRequest = {
  filters: BillProjectHoursRequestFilter;
};

type CustomersQueryResponse = {
  getCustomersRelatedToProjectBilling: Customer[];
};

type OrderConfirmationsQueryResponse = {
  getOrderConfirmationsRelatedToProjectBilling: Contract[];
};

type QueryResponse = CustomersQueryResponse & OrderConfirmationsQueryResponse;

export const useProjectHourBillingRelatedRecords = (
  filters: BillProjectHoursRequestFilter,
  billingBy: ProjectHoursBillingBy
) => {
  const skip = billingBy === ProjectHoursBillingBy.PROJECT;
  const query =
    billingBy === ProjectHoursBillingBy.CUSTOMER
      ? CUSTOMERS_QUERY
      : ORDER_CONFIRMATION_QUERY;

  const response = useQuery<QueryResponse, QueryRequest>(query, {
    variables: { filters },
    skip,
  });

  if (skip) {
    return [];
  }

  return billingBy === ProjectHoursBillingBy.CUSTOMER
    ? sortBy(response.data?.getCustomersRelatedToProjectBilling, 'name')
    : response.data?.getOrderConfirmationsRelatedToProjectBilling ?? [];
};
